import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { Box, TableSortLabel } from '@mui/material';
import type { PCACampaignFramesRequestPayload } from 'c-sdk';
import { EsPCAReportField, PCAReportCountField } from 'c-sdk';
import type { ReportProps, TableReportHeader, TableReportRow } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';

import { Direction } from 'c-types';
import { useDateUtils } from 'c-hooks';
import { useCommonTranslation } from 'c-translation';
import type { ByFieldChartProps } from 'c-main/Components/Campaign/CampaignReports/types';
import LeaderboardFramesDialogES from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/LeaderboardFramesDialogES';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import { ByFieldOverall, withoutExpected } from '../byFieldDataMutators';
import { LeaderboardRows } from './ChartUtils';
import { sortByName } from './sorting';
import { multiFieldLabels } from './multiFieldLabels';

type Props = ByFieldChartProps & {
    filters: PCACampaignFramesRequestPayload['fieldFilters'];
    filterExtras?: React.ReactNode[];
    refineFilters?: boolean;
    showVariance?: boolean;
    includeDisplayCountInLabel?: boolean;
    fields?: EsPCAReportField[];
};

const varianceLabel = 'Variance +/-';

const OverallLeaderboardCharts: React.FC<Props> = ({
    pcaData,
    fieldLabel,
    expectedLabel,
    percentageLabel,
    performanceLabel,
    actualLabel,
    missingLabel,
    inScheduleLabel,
    outScheduleLabel,
    unbookedLabel,
    renderTooltipLabel,
    reportWrapperProps,
    allFields,
    countFields,
    metric,
    field,
    campaignId,
    minDate,
    maxDate,
    searchTerm,
    filters,
    currentStartDate,
    currentTimeframe,
    currentEndDate,
    environment,
    refineFilters = false,
    showVariance = false,
    filterExtras,
    includeDisplayCountInLabel = true,
    fields,
    hideExpected = false,
}) => {
    const t = useCommonTranslation();
    const isMultiField = Array.isArray(fields) && fields.length > 1;
    const groupFieldLabels = useMemo(
        () =>
            isMultiField
                ? (fields as EsPCAReportField[]).map(f =>
                      t(`Modules.Main.Campaigns.Overview.Reports.fields.${f}` as any),
                  )
                : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMultiField, fields],
    );
    const data = useMemo(() => {
        const rows = ByFieldOverall(
            pcaData,
            allFields,
            actualLabel,
            percentageLabel,
            expectedLabel,
            missingLabel,
            fieldLabel,
            inScheduleLabel,
            outScheduleLabel,
            unbookedLabel,
            false,
            includeDisplayCountInLabel,
        )
            .map(row => {
                const base = {
                    ...row,
                    [performanceLabel]: Number(row[percentageLabel]),
                    [varianceLabel]: Number(row[actualLabel]) - Number(row[expectedLabel]),
                };
                if (isMultiField) {
                    return {
                        ...base,
                        ...multiFieldLabels(
                            String(row.name ?? row[fieldLabel] ?? ''),
                            groupFieldLabels,
                            Number((row as any).displayCount ?? 0),
                        ),
                    };
                }
                return base;
            })
            .filter(item => item[fieldLabel] !== '');
        // UL-8204 -- "-" for Expected, % of target and variance; empty in the export.
        return hideExpected
            ? withoutExpected(rows, [
                  expectedLabel,
                  percentageLabel,
                  performanceLabel,
                  varianceLabel,
              ])
            : rows;
    }, [
        pcaData,
        allFields,
        actualLabel,
        percentageLabel,
        expectedLabel,
        missingLabel,
        fieldLabel,
        inScheduleLabel,
        outScheduleLabel,
        unbookedLabel,
        performanceLabel,
        includeDisplayCountInLabel,
        isMultiField,
        groupFieldLabels,
        hideExpected,
    ]);

    const showPerformance = useMemo(
        () =>
            countFields.indexOf(PCAReportCountField.InSchedule) !== -1 &&
            field !== EsPCAReportField.CreativeName,
        [countFields, field],
    );

    const showInSchedule = useMemo(
        () => countFields.indexOf(PCAReportCountField.InSchedule) !== -1,
        [countFields],
    );

    const showOutSchedule = useMemo(
        () => countFields.indexOf(PCAReportCountField.OutSchedule) !== -1,
        [countFields],
    );

    const showUnbooked = useMemo(
        () => countFields.indexOf(PCAReportCountField.UnBooked) !== -1,
        [countFields],
    );

    const [orderByCol, setOrderByCol] = useState(() => {
        if (!showPerformance) return actualLabel;

        return performanceLabel;
    });
    const [orderByDirection, setOrderByDirection] = useState(Direction.DESC);
    const [openField, setOpenField] = useState(null);

    const closeDialog = useCallback(() => {
        setOpenField(null);
    }, []);

    const orderByClicked = useCallback(
        (field: string) => {
            let newDirection;

            let flip = false;
            if (orderByCol === field) {
                flip = true;
            }

            if (flip) {
                newDirection = orderByDirection === Direction.ASC ? Direction.DESC : Direction.ASC;
            } else {
                newDirection = Direction.ASC;
            }

            if (orderByCol !== field) {
                newDirection = Direction.DESC;
            }

            setOrderByCol(field);
            setOrderByDirection(newDirection);
        },
        [orderByCol, orderByDirection],
    );

    const columnOrder = useMemo(() => {
        const cols: string[] = [];
        if (isMultiField) {
            cols.push(...groupFieldLabels);
        } else {
            cols.push(fieldLabel);
        }
        if (showPerformance) cols.push(performanceLabel);
        if (field !== 'creative_name') cols.push(expectedLabel);
        cols.push(actualLabel);
        if (showInSchedule) cols.push(inScheduleLabel);
        if (showOutSchedule) cols.push(outScheduleLabel);
        if (showUnbooked) cols.push(unbookedLabel);
        if (showVariance && field !== 'creative_name') cols.push(varianceLabel);

        return cols;
    }, [
        field,
        fieldLabel,
        showPerformance,
        performanceLabel,
        expectedLabel,
        actualLabel,
        showInSchedule,
        inScheduleLabel,
        showOutSchedule,
        outScheduleLabel,
        showUnbooked,
        unbookedLabel,
        showVariance,
        isMultiField,
        groupFieldLabels,
    ]);

    const headers = useMemo<TableReportHeader[]>(
        () => [
            { content: '#', width: 0 },
            ...columnOrder.map(field => ({
                content: (
                    <TableHeader
                        key={field}
                        onClick={orderByClicked}
                        active={orderByCol === field}
                        field={field}
                        direction={orderByDirection}
                    >
                        {field}
                    </TableHeader>
                ),
            })),
        ],
        [columnOrder, orderByClicked, orderByCol, orderByDirection],
    );

    const filterParts = useMemo(
        () =>
            searchTerm
                .toLowerCase()
                .split('|')
                .filter(p => p.trim().length > 0),
        [searchTerm],
    );

    const filterData = useCallback(
        (data: any[]) => {
            if (searchTerm == null || searchTerm.trim().length === 0) return data;
            return data.filter(dataRow => {
                const fieldLower = dataRow[fieldLabel]?.toLowerCase() ?? '';
                return filterParts.filter(part => fieldLower.indexOf(part) !== -1).length > 0;
            });
        },
        [fieldLabel, searchTerm, filterParts],
    );

    const { dayMonthYearFormat } = useDateUtils();
    const filteredSortedData = useMemo(
        () =>
            sortByName(
                filterData(data),
                orderByDirection,
                orderByCol,
                fieldLabel === orderByCol ? field : null,
                dayMonthYearFormat,
            ),
        [data, field, filterData, orderByCol, orderByDirection, fieldLabel, dayMonthYearFormat],
    );

    const rows = useMemo<TableReportRow[]>(
        () =>
            LeaderboardRows(filteredSortedData, {
                campaignId,
                fieldLabel,
                filterParts,
                metric,
                field,
                actualLabel,
                expectedLabel,
                inScheduleLabel,
                outScheduleLabel,
                unbookedLabel,
                showPerformance,
                percentageLabel,
                showInSchedule,
                showOutSchedule,
                showUnbooked,
                onOpenField: setOpenField,
                columnOrder,
                varianceLabel,
                performanceLabel,
            }),
        [
            filteredSortedData,
            campaignId,
            fieldLabel,
            filterParts,
            metric,
            field,
            actualLabel,
            expectedLabel,
            inScheduleLabel,
            outScheduleLabel,
            unbookedLabel,
            showPerformance,
            percentageLabel,
            showInSchedule,
            showOutSchedule,
            showUnbooked,
            columnOrder,
            performanceLabel,
        ],
    );

    const exportData = useMemo(
        () =>
            filteredSortedData.map(row => ({
                ...row,
                [fieldLabel]: row?.name ?? row?.[fieldLabel],
            })),
        [fieldLabel, filteredSortedData],
    );

    const reportProps = useMemo<ReportProps<unknown>[]>(
        () => [
            {
                type: ReportType.Table,
                namePrefix: `${campaignId}_${metric}_${field}_leaderboard`,
                data: exportData,
                renderTooltipLabel,
                rows,
                headers,
                disableVirtualization: true,
            },
        ],
        [campaignId, exportData, field, headers, metric, renderTooltipLabel, rows],
    );

    const beforeChartComponent = useMemo(
        () => (
            <>
                {reportWrapperProps?.beforeChartComponent && (
                    <Box>{reportWrapperProps.beforeChartComponent}</Box>
                )}
            </>
        ),
        [reportWrapperProps.beforeChartComponent],
    );

    const { source } = useCampaignWrapperState(campaignId);

    const framesDialog = useMemo(
        () => (
            <LeaderboardFramesDialogES
                campaignId={campaignId}
                onClose={closeDialog}
                metric={metric}
                openField={openField}
                fieldValue={openField}
                field={field}
                fields={fields}
                minDate={minDate}
                maxDate={maxDate}
                currentStartDate={currentStartDate}
                currentEndDate={currentEndDate}
                currentTimeframe={currentTimeframe}
                filters={filters}
                environment={environment}
                refineFilters={refineFilters}
                source={source}
            />
        ),
        [
            campaignId,
            closeDialog,
            currentEndDate,
            currentStartDate,
            currentTimeframe,
            environment,
            field,
            fields,
            filters,
            maxDate,
            metric,
            minDate,
            openField,
            refineFilters,
            source,
        ],
    );
    const { onResetAdvancedFilters } = useCampaignWrapperState(campaignId);

    return (
        <>
            <ReportWrapper
                onResetAdvancedFilters={onResetAdvancedFilters}
                reportProps={reportProps}
                {...reportWrapperProps}
                beforeChartComponent={beforeChartComponent}
                reportWrapperProps={{
                    ...reportWrapperProps.reportWrapperProps,
                    maxHeight: '80vh',
                    overflow: 'auto',
                }}
                filterExtras={filterExtras}
            />
            {openField != null && framesDialog}
        </>
    );
};

type HeaderProps = {
    field: string;
    active: boolean;
    direction: Direction;
    onClick: (field: string) => void;
};
const TableHeader: React.FC<PropsWithChildren<HeaderProps>> = ({
    field,
    active,
    direction,
    onClick,
    children,
}) => {
    const orderByClicked = useCallback(() => onClick(field), [onClick, field]);
    return (
        <TableSortLabel direction={direction} active={active} onClick={orderByClicked}>
            {children}
        </TableSortLabel>
    );
};

export default OverallLeaderboardCharts;
